import * as React from 'react';
import { useTranslation } from 'react-i18next';
import moment from "moment";
import { Icon } from '@material-ui/core';
import QdDateDisplay from '../Datetime/QdDateDisplay';
import { RequestControl, TableHeaderColumn, IRelationRowTable, IAllRequest, IEmployeeEntity } from '../Relations/RequestControl';
import lang from '../Relations/RelationsLang';
import { IAccessRelationData } from './AccessDomainModels';
import { IUserAccessDataRow, IDictionary } from './AccessUserAccessesService';
import { IConfig } from './IModels';
import { OptionType } from '../../controls/EmployeesComboboxMI';
import { TableDisplayCell } from '../Relations/TableDisplayCell';
import { RenderRoleCell } from './RenderRoleCell';
import { RenderAlert } from './RenderAlert';
import LogCompanyTooltip from '../LogCompanyTooltip';

interface IAccessRequestContentProps {
	config: IConfig,
	table: React.RefObject<RequestControl>
	name: string,
	value: string,

	blockInsert: boolean,
	blockEditRows: boolean,
	blockCloneRows: boolean,
	blockDeleteRows: boolean,
	editCurrentEmployee: boolean,

	forAcceptant: boolean,
	subordinatesTree: boolean,

	onEditClick: (event: any, row: IRelationRowTable) => void;
	onCopyClick?: (event: any, row: IRelationRowTable) => void;
	onInitData: (rows: IAllRequest) => void;

	employeeRelationsWindow: (row: OptionType) => any;
	changeEmployee: (row: IRelationRowTable, emp: OptionType) => IRelationRowTable;
	onchangedEmployee: (rows: IRelationRowTable[], emp: OptionType) => void;
	onChangedEmployees: (empl: IEmployeeEntity[]) => void,

	rowsPerUsers: IDictionary<IUserAccessDataRow[]>;
}

export default function AccessRequestContent(props: IAccessRequestContentProps) {
	const { t } = useTranslation();

	const fields = props.config.fields;
	return <RequestControl
		ref={props.table}
		name={props.name}
		value={props.value}

		edit={!props.blockEditRows}
		copy={!props.blockCloneRows && !props.blockInsert && !props.blockEditRows}
		remove={!props.blockDeleteRows}

		onInitData={props.onInitData}
		onEditClick={props.onEditClick}
		onCopyClick={props.onCopyClick}

		classNameRow={(row: IAccessRelationData) => {
			if (row.errorMessages && row.errorMessages.length > 0) return "row-danger";
			return "";
		}}

		getTableLabel={(kind: string) => {
			if (kind == "Application") {
				return lang.getDefaultByPrefix(fields.application, "label", "module");
			}
			if (kind == "ServiceAsset") {
				return lang.getDefaultByPrefix(fields.service, "label", "service");
			}
		}}

		editCurrentEmployee={props.editCurrentEmployee}
		employeesFilters={{
			filterId: fields.employee.filterId,
			forAcceptant: props.forAcceptant,
			subordinatesTree: props.subordinatesTree,
			maxRows: fields.employee.maxRows,
		}}
		employeeRelationsWindow={props.employeeRelationsWindow}
		employeesConfig={props.config.fields.employee}
		changeEmployee={props.changeEmployee}
		onchangedEmployee={props.onchangedEmployee}
		onChangedEmployees={props.onChangedEmployees}
	>
		<TableHeaderColumn
			render={(row: IAccessRelationData) => <RenderAlert row={row} />}
		/>

		{(!fields.sensitiveData || !fields.sensitiveData.columnHidden) && <TableHeaderColumn
			render={(row: IAccessRelationData) => <SensitiveDataIcon
				value={row.role?.sensitiveData}
			/>}
		>
			{lang.getDefaultByPrefix(fields.sensitiveData, "label")}
		</TableHeaderColumn>}

		<TableHeaderColumn
			minWidth={220}
			render={(row: IAccessRelationData) => <TableDisplayCell column="resource" errorMessages={row.errorMessages} >
				{row.resource.label}
			</TableDisplayCell>}
		>
			{lang.get("resource")}
		</TableHeaderColumn>

		{!fields.permissionRole.columnHidden && <TableHeaderColumn
			minWidth={fields.permissionRole.columnMinWidth}
			render={row => <TableDisplayCell column="role" errorMessages={row.errorMessages} >
				<RenderRoleCell
					row={row}
					table={props.table.current}
					rowsPerUsers={props.rowsPerUsers}
				/>
			</TableDisplayCell>}
		>
			{lang.getDefaultByPrefix(fields.permissionRole, "label", "onRolePermissions")}
		</TableHeaderColumn>}

		{!fields.login.columnHidden && <TableHeaderColumn className="table-td-data"
			minWidth={fields.login.columnMinWidth}
			render={(row: IAccessRelationData) => <span>{row.login}</span>}
		>
			{lang.getDefaultByPrefix(fields.login, "label", "login")}
		</TableHeaderColumn>}

		{!fields.password.columnHidden && <TableHeaderColumn
			className="table-td-data"
			minWidth={fields.password.columnMinWidth}
			render={(row: IAccessRelationData) => {
				let password = "";
				if (row.password)
					password = "*******";
				return <TableDisplayCell column="password" errorMessages={row.errorMessages} >{password}</TableDisplayCell>;
			}}
		>
			{lang.getDefaultByPrefix(fields.password, "label", "password")}
		</TableHeaderColumn>}

		{!fields.environment.columnHidden && <TableHeaderColumn
			minWidth={fields.environment.columnMinWidth}
			className="table-td-data"
			render={(row: IAccessRelationData) => <TableDisplayCell column="environment" errorMessages={row.errorMessages} >{row.environment}</TableDisplayCell>}
		>
			{lang.getDefaultByPrefix(fields.environment, "label", "environment")}
		</TableHeaderColumn>}

		{!fields.systemName.columnHidden && <TableHeaderColumn
			minWidth={fields.systemName.columnMinWidth}
			className="table-td-data"
			render={(row: IAccessRelationData) => <TableDisplayCell column="systemName" errorMessages={row.errorMessages} >{row.systemName}</TableDisplayCell>}
		>
			{lang.getDefaultByPrefix(fields.systemName, "label", "systemName")}
		</TableHeaderColumn>}

		{!fields.personAccountType.columnHidden && <TableHeaderColumn
			minWidth={fields.personAccountType.columnMinWidth}
			className="table-td-data"
			render={(row: IAccessRelationData) => <TableDisplayCell column="personAccountType" errorMessages={row.errorMessages} >{row.personAccountType}</TableDisplayCell>}
		>
			{lang.getDefaultByPrefix(fields.personAccountType, "label", "personAccountType")}
		</TableHeaderColumn>}

		{!fields.company.columnHidden && <TableHeaderColumn
			minWidth={fields.company.columnMinWidth}
			className="table-td-data"
			render={(row: IAccessRelationData) => {
				return <TableDisplayCell column="company" errorMessages={row.errorMessages}>
					<LogCompanyTooltip value={row.company} />
				</TableDisplayCell>;
			}}
		>
			{lang.getDefaultByPrefix(fields.company, "label", "company")}
		</TableHeaderColumn>}

		{!fields.telephone.columnHidden && <TableHeaderColumn
			minWidth={fields.telephone.columnMinWidth}
			className="table-td-data"
			render={(row: IAccessRelationData) => <TableDisplayCell column="telephone" errorMessages={row.errorMessages} >{row.telephone}</TableDisplayCell>}
		>
			{lang.getDefaultByPrefix(fields.telephone, "label", "telephone")}
		</TableHeaderColumn>}

		{!fields.email.columnHidden && <TableHeaderColumn
			minWidth={fields.email.columnMinWidth}
			className="table-td-data"
			render={(row: IAccessRelationData) => <TableDisplayCell column="email" errorMessages={row.errorMessages} >{row.email}</TableDisplayCell>}
		>
			{lang.getDefaultByPrefix(fields.email, "label", "email")}
		</TableHeaderColumn>}

		{!fields.location.columnHidden && <TableHeaderColumn
			minWidth={fields.location.columnMinWidth}
			className="table-td-data"
			render={(row: IAccessRelationData) => <TableDisplayCell column="location" errorMessages={row.errorMessages} >{row.location}</TableDisplayCell>}
		>
			{lang.getDefaultByPrefix(fields.location, "label", "location")}
		</TableHeaderColumn>}

		{!fields.referenceNumber.columnHidden && <TableHeaderColumn
			minWidth={fields.referenceNumber.columnMinWidth}
			className="table-td-data"
			render={(row: IAccessRelationData) => <TableDisplayCell column="referenceNumber" errorMessages={row.errorMessages} >{row.referenceNumber}</TableDisplayCell>}
		>
			{lang.getDefaultByPrefix(fields.referenceNumber, "label", "referenceNumber")}
		</TableHeaderColumn>}

		{!fields.personAccountDescription.columnHidden && <TableHeaderColumn
			minWidth={fields.personAccountDescription.columnMinWidth}
			className="table-td-data"
			render={(row: IAccessRelationData) => <TableDisplayCell column="personAccountDescription" errorMessages={row.errorMessages} >{row.personAccountDescription}</TableDisplayCell>}
		>
			{lang.getDefaultByPrefix(fields.personAccountDescription, "label", "personAccountDescription")}
		</TableHeaderColumn>}

		{!fields.dateFrom.columnHidden && <TableHeaderColumn
			minWidth={fields.dateFrom.columnMinWidth}
			className="table-td-data"
			render={(row: IAccessRelationData) => <TableDisplayCell column="from" errorMessages={row.errorMessages} ><QdDateDisplay value={row.from} /></TableDisplayCell>}
		>
			{lang.getDefaultByPrefix(fields.dateFrom, "label", "from")}
		</TableHeaderColumn>}

		{!fields.dateTo.columnHidden && <TableHeaderColumn
			minWidth={fields.dateTo.columnMinWidth}
			className="table-td-data"
			render={(row: IAccessRelationData) => {
				let child;
				if (row.relationId)
					child = <DateFieldChange before={row.beforeTo} after={row.to} />;
				else
					child = <QdDateDisplay value={row.to} />

				return <TableDisplayCell column="to" errorMessages={row.errorMessages} >{child}</TableDisplayCell>;

			}}
		>
			{lang.getDefaultByPrefix(fields.dateTo, "label", "to")}
		</TableHeaderColumn>}

		{!fields.description.columnHidden && <TableHeaderColumn
			minWidth={fields.description.columnMinWidth}
			className="table-td-data"
			render={(row: IAccessRelationData) => <TableDisplayCell column="description" errorMessages={row.errorMessages} >{row.description}</TableDisplayCell>}
		>
			{lang.getDefaultByPrefix(fields.description, "label", "description")}
		</TableHeaderColumn>}
	</RequestControl>
}


function DateFieldChange(props: { before: moment.Moment | string, after: moment.Moment | string }) {

	function parse(datetime: moment.Moment | string): moment.Moment {
		if (!datetime)
			return null;
		if (typeof datetime === "string")
			return moment(datetime);

		return datetime;
	}

	const before = parse(props.before);
	const after = parse(props.after);

	if (!before && !after)
		return null;
	if (before?.isSame(after, 'day'))
		return <QdDateDisplay value={after} />;

	if (!before && after)
		return <span className="diff-add"><QdDateDisplay value={after} /></span>;

	if (before && !after)
		return <span className="diff-delete"><QdDateDisplay value={before} /></span>;

	return <>
		<span className="diff-change diff-delete"><QdDateDisplay value={before} /></span>
		<span className="diff-change diff-add"><QdDateDisplay value={after} /></span>
	</>;
}

function SensitiveDataIcon(props: { value: boolean | undefined }) {
	if (!props.value) {
		return null;
	}

	return <Icon title={lang.sensitiveData()}>warning</Icon>
}